import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { AuthService } from '../services/authService';
import { Spinner } from '../components/Spinner';
import {
  consumeReturnUrl,
  getDefaultRouteForRole,
  storeReturnUrl,
} from '../utils/returnUrl';
import { useCurrentUser } from '../hooks/useCurrentUser';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAuthenticated = useIsAuthenticated();
  const { user: currentUser, loading } = useCurrentUser();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const redirectPath = searchParams.get('redirect');
    if (redirectPath) {
      storeReturnUrl(decodeURIComponent(redirectPath));
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isAuthenticated || loading) {
      return;
    }

    const storedReturnUrl = consumeReturnUrl();
    const redirectParam = searchParams.get('redirect');
    const redirectPath = storedReturnUrl || (redirectParam ? decodeURIComponent(redirectParam) : null);

    if (redirectPath) {
      navigate(redirectPath, { replace: true });
      return;
    }

    const defaultRoute = getDefaultRouteForRole(currentUser?.role);
    navigate(defaultRoute, { replace: true });
  }, [isAuthenticated, loading, currentUser, navigate, searchParams]);

  useEffect(() => {
    const expired = searchParams.get('expired') === 'true';
    if (expired) {
      setSessionExpired(true);
      setError('Your session has expired. Please sign in again.');
    }
  }, [searchParams]);

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      setError('');

      const redirectPath = searchParams.get('redirect');
      if (redirectPath) {
        storeReturnUrl(decodeURIComponent(redirectPath));
      }

      await AuthService.login();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="login-container">
        <div className="login-form">
          <div className="login-header">
            <img src="/axis_website_logo_white.svg" alt="Axis" className="login-logo" />
            <h3>Sign In to Axis</h3>
            <p>Access the Axis Healthcare Referral System</p>
          </div>

          {sessionExpired && (
            <div className="alert alert-warning" style={{ marginBottom: '20px' }}>
              Your session has expired. Please sign in again.
            </div>
          )}

          {error && !sessionExpired && (
            <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
              {error}
            </div>
          )}

          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Sign in with your organizational account via Azure Entra ID
            </p>

            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="btn btn-primary btn-login"
              style={{ minWidth: '250px' }}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" />
                  <span style={{ marginLeft: '10px' }}>Connecting...</span>
                </>
              ) : (
                <>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 21 21"
                    style={{ marginRight: '10px', verticalAlign: 'middle' }}
                  >
                    <path
                      d="M1 1h9v9H1V1zm10 0h9v9h-9V1zM1 11h9v9H1v-9zm10 0h9v9h-9v-9z"
                      fill="currentColor"
                    />
                  </svg>
                  Sign in with Microsoft
                </>
              )}
            </button>
          </div>

          <div className="login-info">
            <h4>System Access</h4>
            <div className="access-info">
              <p>This system is for authorized healthcare professionals only.</p>
              <p>Sign in using your Axis Sports Medicine organizational account.</p>
              <p>If you need access, please contact your system administrator.</p>
            </div>
          </div>

          <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
            <p style={{ fontSize: '0.85em', color: '#888', textAlign: 'center' }}>
              Secured by Azure Entra ID SSO
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
